import type { BittsRangePickerChangeValue } from '@crossbeam/bitts';

import type { Dayjs } from 'dayjs';
import { DateTime } from 'luxon';
import { type MaybeRefOrGetter, computed, ref, toValue } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';

const IP_REGEX =
  /^((25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)$/;

export default function useApiKeyCreateForm(
  invalidKeyNames: MaybeRefOrGetter<string[]>,
) {
  const flashesStore = useFlashesStore();

  const keyName = ref('');
  const selectedScopes = ref<string[]>([]);
  const allowedIps = ref<string[]>([]);
  // Keep the picker's value as plain YYYY-MM-DD so round-tripping through
  // the date picker doesn't shift the day in non-UTC zones.
  const expiresDate = ref('');
  const rawKey = ref('');
  const isCreating = ref(false);

  const invalidIps = computed(() =>
    allowedIps.value.filter((ip) => !IP_REGEX.test(ip)),
  );

  const ipsWarning = computed(() =>
    invalidIps.value.length
      ? `Not a valid IPv4 address: ${invalidIps.value.join(', ')}`
      : '',
  );

  const isNameTaken = computed(() => {
    const name = keyName.value.trim().toLowerCase();
    return toValue(invalidKeyNames).some(
      (takenName) => takenName.toLowerCase() === name,
    );
  });

  const canSubmit = computed(
    () =>
      !!keyName.value.trim() &&
      selectedScopes.value.length > 0 &&
      !isNameTaken.value &&
      !invalidIps.value.length,
  );

  function disabledExpiryDate(current: Dayjs) {
    return (
      DateTime.fromJSDate(current.toDate()) <
      DateTime.now().plus({ days: 1 }).startOf('day')
    );
  }

  function onExpiryChange(date: BittsRangePickerChangeValue) {
    if (Array.isArray(date)) return;
    expiresDate.value = date ? date.format('YYYY-MM-DD') : '';
  }

  // Pin expiration to end-of-day UTC. "Expires on June 15" should mean the
  // key works through all of June 15; sending T23:59:59.999Z preserves that,
  // where a bare T00:00:00Z would expire the key the instant the date begins.
  const expiresAt = computed(() =>
    expiresDate.value ? `${expiresDate.value}T23:59:59.999Z` : '',
  );

  async function createKey() {
    if (!canSubmit.value) return false;
    isCreating.value = true;
    try {
      const { data, error } = await crossbeamApi.POST('/v0.1/api-keys', {
        body: {
          name: keyName.value.trim(),
          scopes: selectedScopes.value,
          ...(allowedIps.value.length ? { allowed_ips: allowedIps.value } : {}),
          ...(expiresAt.value ? { expires_at: expiresAt.value } : {}),
        },
      });
      if (error || !data) {
        flashesStore.addErrorFlash({ message: 'Failed to create API key' });
        return false;
      }
      rawKey.value = data.key.raw_key;
      return true;
    } catch (error) {
      captureException(error);
      flashesStore.addErrorFlash({ message: 'Failed to create API key' });
      return false;
    } finally {
      isCreating.value = false;
    }
  }

  return {
    keyName,
    selectedScopes,
    allowedIps,
    expiresDate,
    rawKey,
    isCreating,
    ipsWarning,
    isNameTaken,
    canSubmit,
    disabledExpiryDate,
    onExpiryChange,
    createKey,
  };
}
